import * as React from 'react';
import ReactHotToast from 'react-hot-toast';

import { getErrorMessage } from '@/features/ChatBotV2/components/UploadFiles';
import type {
  AskNeedlReportType,
  SearchWithinType,
} from '@/features/ChatBotV2/context';
import { useChatPanelLogic } from '@/features/ChatBotV2/hooks/useChatPanelLogic';
import type { SessionState } from '@/features/ChatBotV2/types';
import { useFileHandler } from '@/features/ReportGeneration/hooks';
import { useConfigUI } from '@/hooks/query/useFeatureConfig';
import { useFetchUploadConfig } from '@/hooks/useFetchUploadConfig';
import { getMimeTypeFromFileType } from '@/utils/fileAndMimeTypes';

export const useChatPanelCommonProps = ({
  currentFeedOrDocumentId,
  searchWithin,
  sessionState,
  pro,
  report,
  lastActiveSessionId,
}: useChatPanelPropsType) => {
  const listContainerRef = React.useRef<HTMLDivElement>(null);
  const { data: featureConfig } = useConfigUI();

  const {
    isLoading,
    isStreaming,
    isError,
    handleAskQuestion,
    handleClear,
    handleFilesDrop,
    saveToNoteHandler,
    snipNoteMutation,
    stopGenerating,
  } = useChatPanelLogic({
    currentFeedOrDocumentId,
    searchWithin,
    sessionState,
    pro,
    report,
    lastActiveSessionId,
  });

  const { data: uploadConfig } = useFetchUploadConfig({
    source: 'ask_needl',
  });

  const mimeTypes = React.useMemo(
    () =>
      uploadConfig?.file_types.map((fileType) =>
        getMimeTypeFromFileType(fileType)
      ) || [],
    [uploadConfig?.file_types]
  );

  const { isDragging, handleDragOver, handleDragLeave, handleDrop } =
    useFileHandler(
      handleFilesDrop,
      {
        file_types: uploadConfig?.file_types || [],
        file_count_limit: uploadConfig?.file_count_limit || 0,
        file_size_limit: uploadConfig?.file_size_limit || 0,
        mime_types: mimeTypes as string[],
        allow_folder_upload: false,
        file_name_suffixes: [],
      },
      {
        ...(featureConfig?.needl_report_open_research
          ? {
              onSizeLimitExceed: () => {
                ReactHotToast.error(getErrorMessage('file_size_limit'), {
                  duration: 3000,
                  style: {
                    maxWidth: '100%',
                  },
                });
              },
            }
          : {}),
      }
    );

  return {
    isLoading,
    isStreaming,
    isError,
    isDragging,
    handleAskQuestion,
    handleClear,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    saveToNoteHandler,
    snipNoteMutation,
    listContainerRef,
    stopGenerating,
  };
};

export type useChatPanelPropsType = {
  currentFeedOrDocumentId: string;
  searchWithin: SearchWithinType;
  sessionState: SessionState;
  pro: boolean;
  report: AskNeedlReportType;
  lastActiveSessionId: string | null;
  fullWidth?: boolean;
};
